import { useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { Edit, Check } from "@mui/icons-material";
import { useMutation } from "@tanstack/react-query";

import { ConfirmGiftCardContent } from "./ConfirmGiftCardContent";

import { useAppStore } from "@/global/store";
import constants from "@/global/config/constants";
import { useGiftCardApis } from "./useGiftCardApis";
import { useToggle } from "@/shared/base/hooks/useToggle";
import { authOriginUIDs } from "@/global/config/constants";
import { useTeamConfig } from "@/global/hooks/useTeamConfig";
import { decodeBase64String } from "@/global/utils/auth.utils";
import { useGenericModal } from "@/shared/common/GenericModal";
import { selectUser, userState } from "@/global/store/data/user";
import { findAndRetrieveEkataScoreApi } from "@/global/api/cart";
import { isValidEmail, throttleTime } from "@/global/utils/utils";
import { useRouterExtended } from "@/global/hooks/useRouterExtended";
import { dispatchCustomEvent } from "@/global/utils/dispatchCustomEvent";
import { AuthCallback, useTicketmaster } from "@/global/hooks/useTicketmaster";
import { useCheckout202ErrorHandler } from "@/shared/common/useCheckout202ErrorHandler";
import { useDeliveryMethodDatePicker } from "./DeliveryMethodSelector/DeliveryMethodDatePicker";
import { useDeliveryMethodSelectorState } from "./DeliveryMethodSelector/DeliveryMethodSelector";
import { useTransactionErrorGenericModal } from "../PurchaseSeats/modals/TransactionErrorGenericModal";

import type { PurchaseGiftCardProps } from "./PurchaseGiftCard";
import type { GiftCardType, UserType } from "@/global/store/data/types";
import type { PaymentData } from "../GenericPaymentForm/GenericPaymentForm";

export interface TempUserType {
  email: string;
  accountId: string;
}

export type RecipientDetailsType = {
  isRecipientEnabled: boolean;
  email?: string;
  message?: string;
};
export interface GiftCardUrlPayload extends RecipientDetailsType {
  authOrigin: string;
  selectedGiftCardId: GiftCardType["id"];
  tempUser?: TempUserType;
}

export interface GiftPurchaseResponse {
  amount: number;
  couponCode: string;
  orderNumber: number;
  paymentStatus: boolean;
  session?: UserType;
}

export function usePurchaseGiftCard(props: PurchaseGiftCardProps) {
  // ----------------system------------------
  const dispatch = useDispatch();
  const teamConfig = useTeamConfig();
  const { teamSchema, languages, giftCardConfigs, defaultRoute } = teamConfig;
  const router = useRouterExtended();
  const { t } = useTranslation();

  // ----------------apis--------------------
  const {
    getGiftCards,
    handleGetGiftCards,
    checkoutGiftCard,
    getGiftCardSalesByAccountId,
  } = useGiftCardApis();
  const { handleConnectTm } = useTicketmaster();
  const findAndRetrieveEkataScore = useMutation(findAndRetrieveEkataScoreApi);

  // ----------------forms---------------------
  const recipientFormSubmitRef = useRef<HTMLButtonElement>(null);
  const recipientForm = useForm({ shouldFocusError: true });

  // --------------------states--------------------
  const [selectedGiftCard, setSelectedGiftCard] = useState<GiftCardType | null>(
    null,
  );
  const user = useAppStore(selectUser);
  const [isAuthLoading, setAuthLoading] = useState(false);
  const [tempUser, setTempUser] = useState<TempUserType | null>(null);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const urlPayload = decodeBase64String<GiftCardUrlPayload>(
    router.query.payload as string,
  );
  const [purchaseResponse, setPurchaseResponse] =
    useState<GiftPurchaseResponse | null>(null);
  const checkoutErrorModal = useToggle();
  const checkoutSuccessModal = useToggle();
  const transactionErrorGenericModal = useTransactionErrorGenericModal();
  const { handleGiftCheckout202Error } = useCheckout202ErrorHandler();
  const { deliveryDate, set: setDeliveryDate } = useDeliveryMethodDatePicker();
  const { selectedDeliveryMethod, set: setDeliveryMethod } =
    useDeliveryMethodSelectorState();
  const confirmationModal = useGenericModal();

  // ----------------------handlers--------------------------

  // checkout creation handler
  const handleProceedCheckout = async (
    recipientDetails: RecipientDetailsType,
  ) => {
    if (!user) {
      let payload = {
        authOrigin: authOriginUIDs.purchaseGift,
        selectedGiftCardId: selectedGiftCard?.id,
        registerTitle:
          t("auth.register.title.giftCardTitle") ||
          languages?.purchaseGiftCard?.purchaseGiftRegisterPageTitle ||
          "Please Create An Account To Purchase A Gift Card",
      };

      if (recipientDetails.isRecipientEnabled) {
        payload = { ...payload, ...recipientDetails };
      }
      const callback: AuthCallback = {
        pathname: props.authRedirectPath,
        payload,
      };
      if (props.skipRegister) callback.skipRegister = props.skipRegister;

      await handleConnectTm({ callback });
    } else setIsCheckoutOpen(true); // opening checkout
  };

  // handling auth redirect
  const handleAuthRedirect = async (giftCards: GiftCardType[]) => {
    if (!urlPayload) return;
    const {
      authOrigin,
      selectedGiftCardId,
      email,
      message,
      isRecipientEnabled,
      tempUser,
    } = urlPayload;
    const { setValue } = recipientForm;
    if (authOrigin == authOriginUIDs.purchaseGift) {
      const selected = giftCards.find((itm) => itm.id == selectedGiftCardId);
      selected && handleSelectGiftCard(selected);
      isRecipientEnabled && setValue("isRecipientEnabled", isRecipientEnabled);
      email && setValue("email", email);
      message && setValue("message", message);
      tempUser && setTempUser(tempUser);
      setIsCheckoutOpen(true); // opening checkout
    }
  };

  // handling checkout
  const handleCheckout = async (paymentData: PaymentData) => {
    const recipient = recipientForm.getValues() as RecipientDetailsType;

    if (!recipient.email || !isValidEmail(recipient.email)) {
      recipientForm.trigger("email");
      recipientForm.setFocus("email");
      return;
    }

    if (selectedDeliveryMethod === "sendLater" && !deliveryDate) {
      return setDeliveryDate({ isError: true });
    }

    let payload = Object.create(null);
    payload.teamSchema = teamSchema;

    if (user) {
      const { token, accountGroupIds, version, ...restUser } = user as any;
      payload.user = restUser;
    }
    if (tempUser) payload.user = tempUser;

    if (
      recipient &&
      recipient?.email &&
      payload?.user.email &&
      payload?.user.email.toLowerCase().trim() ===
        recipient.email.toLowerCase().trim()
    ) {
      return transactionErrorGenericModal.set({
        isOpen: true,
        errorMessage:
          t("giftCard.checkout.recipeientEmailSameAsYourEmailError") ||
          "Recipient email should be different from your account email.",
      });
    }

    payload.sendNow = true;
    payload.giftCardId = selectedGiftCard?.id;
    payload.recipientDetails = {
      email: recipient?.email,
      message: recipient?.message || undefined,
    };

    // gift card amount allowed check
    // if config is missing, api will be skiped
    // prettier-ignore
    if (giftCardConfigs?.maxGiftPurchaseCountAllowed && giftCardConfigs?.maxGiftPurchaseCountAllowed > 0) {
      try {
        const payload = { teamSchema, accountId: Number(user?.accountId) || Number(tempUser?.accountId) };
        const { data } = await getGiftCardSalesByAccountId.mutateAsync(payload);
        if (data.giftCardsPurchasedCount >= giftCardConfigs.maxGiftPurchaseCountAllowed) {
          return transactionErrorGenericModal.set({
            isOpen: true,
            errorMessage: t("giftCard.checkout.giftCardAmountLimitExceededError") || languages?.purchaseGiftCard?.giftCardAmountLimitExceededError || "Your transaction cannot be completed online. Please call the box office to complete your purchase.",
          });
        }
      } catch (error) {
        return checkoutErrorModal.onOpen();
      }
    }

    if (paymentData.cardDetails) payload.cardDetail = paymentData.cardDetails;
    if (paymentData.savedCard) payload.savedCard = paymentData.savedCard;

    if (selectedDeliveryMethod === "sendLater" && deliveryDate) {
      payload.sendNow = false;
      payload.sendDate = deliveryDate;
    }

    if (!!paymentData.cardDetails) {
      // ekata validation
      try {
        const { data: ekataResponse } =
          await findAndRetrieveEkataScore.mutateAsync({
            teamSchema,
            cardDetail: {
              address: paymentData.cardDetails.address,
              zipCode: paymentData.cardDetails.zipCode,
              firstName: paymentData.cardDetails.firstName,
              lastName: paymentData.cardDetails.lastName,
              city: paymentData.cardDetails.city,
              country: paymentData.cardDetails.country,
              state: paymentData.cardDetails.state,
            },
            user: { ...payload.user },
          });

        if (!ekataResponse?.isValid) {
          return transactionErrorGenericModal.set({
            isOpen: true,
            errorMessage: ekataResponse?.message,
          });
        }
      } catch (error) {
        return checkoutErrorModal.onOpen();
      }
    }

    try {
      await checkoutGiftCard.mutateAsync(payload, {
        onSuccess: async ({ data }) => {
          try {
            setPurchaseResponse(data);
            setIsCheckoutOpen(false);
            if (data?.session) {
              dispatch(
                userState.actions.set({ teamSchema, session: data.session }),
              );
            }
            checkoutSuccessModal.onOpen();
            dispatchCustomEvent(constants.customEvents.giftPurchaseSuccess, {
              checkoutRes: data,
            });
            await cleanUpCheckoutSuccessSession();
          } catch (error) {
            console.log(error);
          }
        },
        onError: async (error) => {
          const { is202Error } = await handleGiftCheckout202Error(error);
          if (is202Error) return setIsCheckoutOpen(false);
          checkoutErrorModal.onOpen();
        },
      });
    } catch (err) {
      console.log(err);
    }
  };

  function cleanUpCheckoutSuccessSession() {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        try {
          setSelectedGiftCard(null);
          recipientForm.reset();
          handleRemoveUrlPayload();
          return resolve(true);
        } catch (error) {
          console.log("error in cleanup", error);
          return reject(error);
        }
      }, 100);
    });
  }

  // handling successModal close with resetting all states
  function handleSuccessModalClose() {
    checkoutSuccessModal.onClose();
    setPurchaseResponse(null);
    setDeliveryMethod({ selectedDeliveryMethod: "sendNow" });
    setDeliveryDate({ deliveryDate: null, isError: false });
  }

  // gift card selection handler
  const handleSelectGiftCard = (giftCard: GiftCardType) => {
    if (selectedGiftCard?.id == giftCard.id) return;
    setSelectedGiftCard(giftCard);
  };

  // handler for footer checkout button
  const handleClickCheckout = () => {
    recipientFormSubmitRef?.current?.click();
  };

  function handleRemoveUrlPayload() {
    // removing router payload
    if (router?.query?.payload) {
      let query = { ...router.query };
      delete query.payload;
      router.push({ query });
    }
  }

  function handleOpenConfirmationModal(paymentData: PaymentData) {
    const recipient = recipientForm.getValues() as RecipientDetailsType;

    if (!recipient.email || !isValidEmail(recipient.email)) {
      recipientForm.trigger("email");
      recipientForm.setFocus("email");
      return;
    }

    if (selectedDeliveryMethod === "sendLater" && !deliveryDate) {
      return setDeliveryDate({ isError: true });
    }

    confirmationModal.set({
      isOpen: true,
      titleIconVariant: "info",
      title:
        t("giftCard.checkout.confirmationModal.title") ||
        "Confirm Gift Card Purchase",
      description: <ConfirmGiftCardContent {...recipient} />,
      closeIconProps: {
        isDisabled:
          checkoutGiftCard.isLoading ||
          findAndRetrieveEkataScore.isLoading ||
          getGiftCardSalesByAccountId.isLoading,
        onClick: () => confirmationModal.set({ isOpen: false }),
      },
      closeButtonProps: {
        variant: "outlined",
        endIcon: <Edit />,
      },
      closeButtonText:
        t("giftCard.checkout.confirmationModal.editButtonText") || "Edit",
      confirmButtonText:
        t("giftCard.checkout.confirmationModal.confirmButtonText") || "Confirm",
      confirmButtonProps: {
        endIcon: <Check />,
      },
      onClickClose: () => confirmationModal.set({ isOpen: false }),
      onClickConfirm: async () => {
        confirmationModal.set({
          isCloseButtonDisabled: true,
          isConfirmButtonLoading: true,
        });

        await handleCheckout(paymentData);

        confirmationModal.set({ isOpen: false });
        throttleTime(400, () => confirmationModal.reset());
      },
    });
  }

  return {
    apis: {
      getGiftCards,
      checkoutGiftCard,
      findAndRetrieveEkataScore,
      getGiftCardSalesByAccountId,
    },
    states: {
      isCheckoutOpen,
      recipientFormSubmitRef,
      recipientForm,
      selectedGiftCard,
      isAuthLoading,
      tempUser,
      purchaseResponse,
      checkoutErrorModal,
      checkoutSuccessModal,
      deliveryDate,
    },
    handlers: {
      setIsCheckoutOpen,
      handleGetGiftCards,
      handleSelectGiftCard,
      handleProceedCheckout,
      handleClickCheckout,
      handleAuthRedirect,
      handleCheckout,
      handleSuccessModalClose,
      handleRemoveUrlPayload,
      handleOpenConfirmationModal,
    },
  };
}
